import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useLocationAutocomplete } from '@/hooks/use-location-autocomplete';
import { PlaceInterface } from '@/types/api-types';
import { KnownQueryParams, QUERY_PARAM } from '@/utils/helpers/api-urls/constants';

interface UseQueryLocationProps {
  paramsToWatch: KnownQueryParams[];
  onParamsDidUpdate: (params: { [key in KnownQueryParams]?: any }) => void;
}

/**
 * Will watch for given query params and call update function when they change
 * Also handles the case of origin and destination which require a data fetch
 */
export const useWatchQueryParams = ({ paramsToWatch, onParamsDidUpdate }: UseQueryLocationProps) => {
  const [isReady, setIsReady] = useState(false);
  const queryParams = useSearchParams() || undefined;
  const [originQuery, setOriginQuery] = useState<string>();
  const [destinationQuery, setDestinationQuery] = useState<string>();
  const [values, setValues] = useState<Record<string, string>>();
  const [origin, setOrigin] = useState<PlaceInterface>();
  const [destination, setDestination] = useState<PlaceInterface>();

  const { suggestions: originSuggestion, isLoading: isOriginLoading } = useLocationAutocomplete({
    name: QUERY_PARAM.ORIGIN,
    query: originQuery,
    shouldKeepPreviousData: false,
  });
  const { suggestions: destinationSuggestion, isLoading: isDestinationLoading } = useLocationAutocomplete({
    name: QUERY_PARAM.DESTINATION,
    query: destinationQuery,
    shouldKeepPreviousData: false,
  });

  // Filter origin and destination since we handle those in a *special* way
  const filteredParamsToWatch = paramsToWatch.filter(
    (param) => !([QUERY_PARAM.ORIGIN, QUERY_PARAM.DESTINATION] as string[]).includes(param),
  );
  const shouldWatchOrigin = paramsToWatch.includes(QUERY_PARAM.ORIGIN);
  const shouldWatchDestination = paramsToWatch.includes(QUERY_PARAM.DESTINATION);

  useEffect(() => {
    if ((isOriginLoading || isDestinationLoading) && !isReady) return;

    const params = new URLSearchParams(queryParams);
    const queryValues = filteredParamsToWatch.reduce((acc, param) => {
      return { ...acc, [param]: params.get(param) };
    }, {});
    const originParam = shouldWatchOrigin ? params.get(QUERY_PARAM.ORIGIN) || undefined : undefined;
    const destinationParam = shouldWatchDestination ? params.get(QUERY_PARAM.DESTINATION) || undefined : undefined;

    setOriginQuery(originParam);
    setDestinationQuery(destinationParam);
    setValues(queryValues);
    setIsReady(true);
  }, [queryParams, isOriginLoading, isDestinationLoading, isReady]);

  useEffect(() => {
    if (paramsToWatch.includes('origin') || paramsToWatch.includes('destination')) {
      setOrigin(originSuggestion.length ? originSuggestion[0] : undefined);
      setDestination(destinationSuggestion.length ? destinationSuggestion[0] : undefined);
    }
  }, [originSuggestion, destinationSuggestion, originQuery, destinationQuery]);

  useEffect(() => {
    onParamsDidUpdate({ origin, destination, ...values });
  }, [values, origin, destination]);
};
